import { useEffect, useRef, useState, useCallback } from 'react';
import { buildChatWsUrl } from '../lib/chatAPI';

/**
 * Hook de conexão WebSocket do chat.
 * - Reconecta automaticamente com backoff (1s, 2s, 4s, ... até 30s)
 * - Ping a cada 25s para manter viva a conexão
 * - Detecta inatividade do usuário (mouse/teclado) e sinaliza "ausente" após 5 min
 * - Chama `onEvent(payload)` para cada mensagem recebida
 *
 * Retorna: { connected, sendJson, forceStatus }
 */
export function useChatSocket({ enabled, token, onEvent, awayAfterMs = 5 * 60 * 1000 }) {
  const wsRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const idleTimer = useRef(null);
  const stoppedRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [effectiveStatus, setEffectiveStatus] = useState('online');
  const manualStatusRef = useRef(null); // se usuário setou manualmente

  const sendJson = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  const forceStatus = useCallback((status) => {
    manualStatusRef.current = status;
    setEffectiveStatus(status);
  }, []);

  // Detecção de atividade (mouse/teclado) — reseta timer de "ausente"
  useEffect(() => {
    if (!enabled) return undefined;

    const resetIdle = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      // Se estava ausente (não manual), volta pra online
      if (manualStatusRef.current === null && effectiveStatus === 'ausente') {
        setEffectiveStatus('online');
        sendJson({ type: 'presence', status: 'online' });
      }
      idleTimer.current = setTimeout(() => {
        if (manualStatusRef.current === null) {
          setEffectiveStatus('ausente');
          sendJson({ type: 'presence', status: 'ausente' });
        }
      }, awayAfterMs);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdle));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [enabled, awayAfterMs, effectiveStatus, sendJson]);

  // Conexão WS + reconexão
  useEffect(() => {
    if (!enabled || !token) return undefined;
    stoppedRef.current = false;

    const connect = () => {
      if (stoppedRef.current) return;
      try {
        const ws = new WebSocket(buildChatWsUrl(token));
        wsRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          reconnectAttempt.current = 0;
          // Envia status ativo atual
          const status = manualStatusRef.current || 'online';
          ws.send(JSON.stringify({ type: 'presence', status }));
          // Ping periódico
          if (pingTimer.current) clearInterval(pingTimer.current);
          pingTimer.current = setInterval(() => {
            try { ws.send(JSON.stringify({ type: 'ping' })); } catch (e) { void e; }
          }, 25000);
        };

        ws.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (onEvent) onEvent(data);
          } catch (e) { void e; }
        };

        ws.onclose = () => {
          setConnected(false);
          if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
          if (stoppedRef.current) return;
          // backoff exponencial 1s..30s
          const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempt.current));
          reconnectAttempt.current += 1;
          reconnectTimer.current = setTimeout(connect, delay);
        };

        ws.onerror = () => {
          try { ws.close(); } catch (e) { void e; }
        };
      } catch (e) {
        setConnected(false);
      }
    };

    connect();

    return () => {
      stoppedRef.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
      const ws = wsRef.current;
      if (ws) { try { ws.close(); } catch (e) { void e; } }
      wsRef.current = null;
    };
  }, [enabled, token, onEvent]);

  return { connected, sendJson, forceStatus, effectiveStatus };
}
